'use client'

import { faLock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { zodResolver } from '@hookform/resolvers/zod'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import { useForm } from 'react-hook-form'
import { SOCIAL_LOGIN_URL } from '@/constants/auth'
import { useLogin } from '@/hooks/business/auth/useLogin'
import { type ISignInForm, SignInFormSchema } from '@/schemas/auth/authFormSchemas'
import Button from '~/components/Form/Button'
import Input from '~/components/Form/Input'

/** Destino pedido pelo proxy (`/login?redirect=<caminho>`); o `getPostLoginPath` só aceita caminho da loja. */
const readRedirect = () => new URLSearchParams(window.location.search).get('redirect') ?? undefined

const Login: React.FC = () => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ISignInForm>({ resolver: zodResolver(SignInFormSchema) })

    const { login } = useLogin()

    // O aviso de credencial errada sai no service; a rejeição só encerra o envio.
    const handleSignIn = (data: ISignInForm) => login(data, readRedirect()).catch(() => undefined)

    return (
        <div className="container mx-auto mt-10 max-md:mb-12">
            <div className="flex flex-row items-center justify-center gap-12 max-md:flex-col pb-36">
                <div className="basis-1/3 max-md:basis-1">
                    <h4 className="pb-8 color-primary">
                        <strong>Faça login usando seu email e senha</strong>
                    </h4>
                    <form method="post" data-bitwarden-watching="1" onSubmit={handleSubmit(handleSignIn)}>
                        <Input labelText="E-mail" register={register('email')} error={errors.email?.message} />
                        <Input
                            labelText="Senha"
                            type="password"
                            register={register('password')}
                            error={errors.password?.message}
                        />
                        <div className="form-group">
                            <Link className="col-form-label-sm" href="/esqueci-minha-senha">
                                <FontAwesomeIcon icon={faLock} />
                                <ins>Esqueci a senha!</ins>
                            </Link>
                        </div>
                        <br />
                        <Button size="lg" type="submit" className="w-full">
                            ENTRAR
                        </Button>
                    </form>
                </div>
                <div className="flex flex-col items-center justify-center w-10 max-md:hidden">
                    <h3 className="line">
                        <span className="text-line">
                            <strong>OU</strong>
                        </span>
                    </h3>
                </div>
                <div className="flex flex-col items-center h-full gap-20 basis-1/3 max-md:basis-1">
                    <div className="pb-16 border-b border-calm-gray-ultralight">
                        <h6 className="pb-3">Faça login utilizando sua rede social</h6>
                        <Link href={SOCIAL_LOGIN_URL}>
                            <Button size="lg" color="white">
                                <Image
                                    src="/assets/icons/google.svg"
                                    height={24}
                                    width={24}
                                    alt="icone do Gooogle"
                                    className="mr-3"
                                    id="searchBtn"
                                />
                                GOOGLE
                            </Button>
                        </Link>
                    </div>
                    <div>
                        <h6 className="pb-3">Ainda não é cliente?</h6>
                        <Link href="/cadastro">
                            <Button size="lg">CADASTRE-SE</Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Login
